import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
} from "@mui/material";
import { partnerService, Partner } from "../../apis/partners";
import { offerTypeService, OfferType } from "../../apis/offerTypes";

interface PartnerDetailsModalProps {
  open: boolean;
  onClose: () => void;
  partnerId: string | undefined;
}

const PartnerDetailsModal: React.FC<PartnerDetailsModalProps> = ({ open, onClose, partnerId }) => {
  const [partner, setPartner] = useState<Partner | null>(null);
  const [offerTypes, setOfferTypes] = useState<OfferType[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && partnerId) {
      setPartner(null); // Reset partner state when opening modal
      fetchPartnerDetails();
      fetchOfferTypes();
    } else if (!open) {
      // Reset state when modal closes
      setPartner(null);
      setLoading(false);
    }
  }, [open, partnerId]);

  const fetchOfferTypes = async () => {
    try {
      const response = await offerTypeService.getActiveOfferTypes();
      if (response.status === "success" || response.status === true) {
        setOfferTypes(response.data.offer_types || []);
      }
    } catch (error) {
      console.error("Failed to fetch offer types:", error);
    }
  };

  const getOfferTypeName = (offerTypeId: string) => {
    const offerType = offerTypes.find((ot) => ot._id === offerTypeId);
    return offerType ? offerType.name : offerTypeId;
  };

  const fetchPartnerDetails = async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const data = await partnerService.getPartnerById(partnerId);
      console.log("Partner details response:", data);
      // Set partner if we have valid data (check for _id or other required fields)
      if (data && (data._id || data.email)) {
        setPartner(data);
      } else {
        console.error("Invalid partner data received:", data);
        setPartner(null);
      }
    } catch (error: any) {
      console.error("Failed to fetch partner details:", error);
      console.error("Error response:", error.response?.data);
      setPartner(null);
    } finally {
      setLoading(false);
    }
  };

  // Show loading or error state only when modal is open
  if (open && !partner && !loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Partner Details</DialogTitle>
        <DialogContent>
          <Typography>Partner not found</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Show loading state
  if (open && loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Partner Details</DialogTitle>
        <DialogContent>
          <Typography>Loading...</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Don't render anything if modal is closed or no partner data
  if (!open || !partner) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Partner Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Partner Name
              </Typography>
              <Typography variant="body1">{`${partner.firstName} ${partner.lastName}`}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Company Name
              </Typography>
              <Typography variant="body1">{partner.companyName || "-"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Email
              </Typography>
              <Typography variant="body1">{partner.email}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Phone
              </Typography>
              <Typography variant="body1">{partner.phone}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Offer Type
              </Typography>
              <Chip
                label={getOfferTypeName(partner.offerTypeId)}
                color="primary"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Agency Code
              </Typography>
              <Typography variant="body1">{partner.agencyCode}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">
                Country
              </Typography>
              <Typography variant="body1">{partner.countryCode}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Cars Count
              </Typography>
              <Typography variant="h6">{partner.carsCount || 0}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Drivers Count
              </Typography>
              <Typography variant="h6">{partner.driversCount || 0}</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="subtitle2" color="textSecondary">
                Total Earnings
              </Typography>
              <Typography variant="h6">{(partner.totalEarnings || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} FCFA</Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnerDetailsModal;

